import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { setDoc, useDoc } from '../store/store';
import { desligarAgenda, listarAgendas, pedirPermissao, sincronizarAgenda, souOSincronizador, type Agenda, type Resultado } from '../lib/gcal';
import { idAparelho, nomeAparelho } from '../lib/aparelho';
import { brDateTime } from '../lib/date';
import { t } from '../lib/i18n';
import { Sheet } from './ui';
import { BotaoAcao, Divisor, LinhaChave, LinhaOpcoes } from './ajustes/Linhas';
import { C, alpha } from './theme';

const WEB = Platform.OS === 'web';
const ignorarPorPadrao = (a: Agenda) => /feriad|holiday|aniversár|birthday|contacts|contatos|semana|week number/i.test(a.titulo);

/** Ajustes > Agenda do Google: liga a sincronização neste celular, escolhe as agendas e mostra o andamento. */
export function AgendaGoogle({ cor }: { cor: string }) {
  const aj = useDoc('ajustes');
  const cfg = aj.gcal ?? null;
  const [souEu, setSouEu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lista, setLista] = useState<Agenda[] | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [destino, setDestino] = useState<string | null>(null);
  const [enviar, setEnviar] = useState(true);
  const [existentes, setExistentes] = useState(false);

  useEffect(() => { souOSincronizador(cfg).then(setSouEu); }, [cfg?.aparelho]); // eslint-disable-line react-hooks/exhaustive-deps

  const mostrar = (r: Resultado | null) => {
    if (!r) return;
    if (r.erro) Alert.alert(t('Agenda do Google'), r.erro);
    else Alert.alert(t('Agenda sincronizada ✓'), t('{novas} nova(s), {atualizadas} atualizada(s), {enviadas} enviada(s), {removidas} removida(s)', { ...r }));
  };

  async function abrirEscolha() {
    if (!(await pedirPermissao())) {
      return Alert.alert(t('Agenda não liberada'), t('Para sincronizar, libere o acesso à agenda para o My Planner nas configurações do celular.'));
    }
    setBusy(true);
    try {
      const ags = await listarAgendas();
      if (!ags.length) return Alert.alert(t('Nenhuma agenda encontrada'), t('Confira se a sua conta Google está adicionada no celular e com a agenda sincronizando.'));
      const mesmoAparelho = souEu && cfg;
      setSel(mesmoAparelho ? cfg!.importar.filter(id => ags.some(a => a.id === id)) : (ags.some(a => a.google && a.titulo === a.conta) ? ags.filter(a => a.google && a.titulo === a.conta) : ags.filter(a => a.google && !ignorarPorPadrao(a))).map(a => a.id));
      const editaveis = ags.filter(a => a.editavel);
      setDestino(mesmoAparelho && cfg!.destino ? cfg!.destino : (editaveis.find(a => a.google && a.titulo === a.conta) ?? editaveis.find(a => a.google) ?? editaveis[0])?.id ?? null);
      setEnviar(mesmoAparelho ? cfg!.enviar : true);
      setExistentes(false);
      setLista(ags);
    } finally { setBusy(false); }
  }

  async function salvar() {
    if (!lista) return;
    if (!sel.length && !(enviar && destino)) return Alert.alert(t('Escolha ao menos uma agenda.'));
    const id = await idAparelho();
    const trocaDeAparelho = !!cfg && cfg.aparelho !== id;
    if (trocaDeAparelho) desligarAgenda(false);     // os vínculos do outro celular não valem aqui; serão refeitos
    const agora = new Date().toISOString();
    setDoc('ajustes', d => ({ ...d, gcal: {
      aparelho: id, nomeAparelho: nomeAparelho(), importar: sel,
      nomes: Object.fromEntries(lista.map(a => [a.id, a.titulo])), destino: enviar ? destino : null, enviar,
      desde: existentes ? '0000' : (!trocaDeAparelho && d.gcal?.desde) || agora, ultima: d.gcal?.ultima ?? null, resumo: d.gcal?.resumo ?? null,
    } }));
    setLista(null);
    setSouEu(true);
    setBusy(true);
    try { mostrar(await sincronizarAgenda()); } finally { setBusy(false); }
  }

  async function agora() {
    setBusy(true);
    try { mostrar(await sincronizarAgenda()); } finally { setBusy(false); }
  }

  const desligar = () => Alert.alert(t('Desligar a sincronização?'), t('As tarefas continuam no My Planner. Quer apagar também as que vieram da agenda do Google?'), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Manter tarefas'), onPress: () => desligarAgenda(false) },
    { text: t('Apagar as da agenda'), style: 'destructive', onPress: () => desligarAgenda(true) },
  ]);

  return (
    <>
      {!cfg && (
        <>
          <Text style={{ color: C.text }}>{t('Traga os compromissos da sua agenda do Google para o My Planner e edite por aqui: as mudanças voltam para a agenda.')}</Text>
          {WEB
            ? <Text style={{ color: C.sub, fontSize: 12 }}>{t('Para ligar, use o app no celular (Ajustes › Agenda e mapas › Agenda do Google). Depois, tudo aparece aqui também.')}</Text>
            : <BotaoAcao cheio cor={cor} label={t('📆 Conectar a agenda deste celular')} onPress={abrirEscolha} disabled={busy} />}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Usa a conta Google que já está no celular. Nenhuma senha do Google é pedida pelo My Planner.')}</Text>
        </>
      )}

      {cfg && (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 20 }}>📆</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{t('Sincronizando pelo dispositivo {nome}', { nome: cfg.nomeAparelho })}{souEu ? ` (${t('este')})` : ''}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>
                {cfg.importar.map(id => cfg.nomes[id] ?? id).join(' · ') || t('Nenhuma agenda para trazer')}
              </Text>
            </View>
          </View>
          {cfg.enviar && cfg.destino && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Tarefas novas do My Planner vão para: {agenda}', { agenda: cfg.nomes[cfg.destino] ?? '' })}</Text>}
          <Text style={{ color: C.mute, fontSize: 12 }}>
            {cfg.ultima ? t('Última sincronização: {quando}', { quando: brDateTime(cfg.ultima) }) + (cfg.resumo ? ` · ${cfg.resumo}` : '') : t('Ainda não sincronizado')}
          </Text>
          {(aj.gcalApagar?.length ?? 0) > 0 && <Text style={{ color: C.warn, fontSize: 12 }}>{t('{n} exclusão(ões) esperando o celular sincronizar.', { n: aj.gcalApagar!.length })}</Text>}
          {busy && <ActivityIndicator color={cor} />}
          {!WEB && souEu && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 8 }}>
              <BotaoAcao cheio cor={cor} label={'🔄 ' + t('Sincronizar agora')} onPress={agora} disabled={busy} />
              <BotaoAcao cor={cor} label={t('Escolher agendas')} onPress={abrirEscolha} disabled={busy} />
              <BotaoAcao cor={C.danger} label={t('Desligar')} onPress={desligar} />
            </View>
          )}
          {!WEB && !souEu && (
            <>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Só um celular sincroniza com a agenda. Para passar para este, toque abaixo.')}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 8 }}>
                <BotaoAcao cheio cor={cor} label={'📱 ' + t('Usar este celular')} onPress={abrirEscolha} disabled={busy} />
                <BotaoAcao cor={C.danger} label={t('Desligar')} onPress={desligar} />
              </View>
            </>
          )}
          {WEB && <BotaoAcao cor={C.danger} label={t('Desligar')} onPress={desligar} />}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('A sincronização roda ao abrir o app e depois de cada alteração. Tarefas ligadas à agenda mostram 📆.')}</Text>
        </>
      )}

      <Sheet visible={!!lista} onClose={() => setLista(null)} title={t('Agenda do Google')}
        right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
        <ScrollView contentContainerStyle={{ padding: 16, gap: 10, paddingBottom: 60 }}>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{t('TRAZER PARA O MY PLANNER')}</Text>
          {(lista ?? []).map(a => {
            const on = sel.includes(a.id);
            return (
              <Pressable key={a.id} onPress={() => setSel(s => (on ? s.filter(x => x !== a.id) : [...s, a.id]))}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14, backgroundColor: on ? alpha(cor, 0.15) : C.card }}>
                <View style={{ width: 14, height: 14, borderRadius: 7, backgroundColor: a.cor }} />
                <View style={{ flex: 1 }}>
                  <Text style={{ color: C.text, fontWeight: '600' }}>{a.titulo}</Text>
                  <Text style={{ color: C.sub, fontSize: 11 }}>{a.conta}{a.google ? ' · Google' : ''}</Text>
                </View>
                <Text style={{ color: on ? cor : C.mute, fontSize: 18 }}>{on ? '☑' : '☐'}</Text>
              </Pressable>
            );
          })}

          <View style={{ backgroundColor: C.card, borderRadius: 14, paddingHorizontal: 12, paddingVertical: 4, marginTop: 12 }}>
            <LinhaChave icone="📤" titulo={t('Enviar tarefas do My Planner para a agenda')} cor={cor} valor={enviar} onMudar={setEnviar} />
            {enviar && (<>
              <Divisor />
              <LinhaOpcoes icone="📅" titulo={t('Em qual agenda criar os eventos:').replace(/\s*:\s*$/, '')} valor={destino ?? ''}
                rotulo={(lista ?? []).find(a => a.id === destino)?.titulo ?? '—'}
                opcoes={(lista ?? []).filter(a => a.editavel).map(a => [a.id, a.titulo] as [string, string])} onEscolher={setDestino} />
            </>)}
          </View>
          {enviar && (
            <>
              <Pressable onPress={() => setExistentes(v => !v)} style={{ flexDirection: 'row', gap: 10, alignItems: 'center', marginTop: 4 }}>
                <Text style={{ fontSize: 18, color: C.text }}>{existentes ? '☑' : '☐'}</Text>
                <Text style={{ color: C.text, flex: 1 }}>{t('Enviar também as tarefas que já existem (de hoje em diante)')}</Text>
              </Pressable>
            </>
          )}
          <View style={{ marginTop: 4 }}><BotaoAcao cheio cor={cor} label={t('Salvar e sincronizar')} onPress={salvar} /></View>
        </ScrollView>
      </Sheet>
    </>
  );
}
